import { useState } from 'react';
import { afsluitMelding } from '@qc/core';
import { Knop } from '@qc/ui';
import type { EvenementBeeld } from './useEvenement.js';

export interface AfsluitknopProps {
  beeld: EvenementBeeld;
  alAfgesloten: boolean;
  onAfgesloten: () => void;
}

/**
 * Eén knop, met bevestiging. Hij weigert zolang er ergens nog bonnen wachten of
 * een kassa te lang stil is — anders sluit je af terwijl er nog omzet onderweg
 * is, en dan klopt de afrekening niet.
 */
export function Afsluitknop({ beeld, alAfgesloten, onAfgesloten }: AfsluitknopProps) {
  const [bevestigen, setBevestigen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [geweigerd, setGeweigerd] = useState(false);

  if (alAfgesloten) {
    return (
      <div className="rounded-tegel border border-rand bg-vlak p-6">
        <h2 className="mb-1 text-xl font-bold">Afgesloten</h2>
        <p className="text-tekst-zacht">
          Het evenement is afgesloten. De afrekening en de uitbetaling volgen binnen één werkdag.
        </p>
      </div>
    );
  }

  const controle = beeld.afsluitcontrole;

  return (
    <div className="rounded-tegel border border-rand bg-vlak p-6">
      <h2 className="mb-1 text-xl font-bold">Evenement afsluiten</h2>
      <p className="mb-4 text-tekst-zacht">{afsluitMelding(controle)}</p>

      {controle.blokkades.length > 0 && (
        <ul className="mb-4 grid gap-2">
          {controle.blokkades.map((blokkade) => (
            <li
              key={blokkade.kraamdeelnameId}
              className="rounded-tegel bg-vlak-hoog p-4 text-waarschuwing"
            >
              {blokkade.kraamnaam} {blokkade.reden}
            </li>
          ))}
        </ul>
      )}

      {geweigerd && (
        <p className="mb-4 text-lg text-waarschuwing">
          Er kwam net nog iets binnen. Kijk hierboven wat er nog loopt en probeer het zo opnieuw.
        </p>
      )}

      {!bevestigen ? (
        <Knop
          nadruk={controle.magAfsluiten ? 'hoofd' : 'gewoon'}
          disabled={!controle.magAfsluiten}
          onClick={() => setBevestigen(true)}
          className="w-full"
        >
          Evenement afsluiten
        </Knop>
      ) : (
        <div className="grid gap-3">
          <p className="text-lg">
            Weet je het zeker? Na afsluiten kan er niets meer worden aangeslagen en gaat de
            afrekening lopen.
          </p>
          <div className="flex gap-3">
            <Knop
              nadruk="hoofd"
              disabled={bezig}
              className="flex-1"
              onClick={() => {
                setBezig(true);
                setGeweigerd(false);
                void beeld.sluitAf().then((gelukt) => {
                  setBezig(false);
                  setBevestigen(false);
                  if (gelukt) onAfgesloten();
                  else setGeweigerd(true);
                });
              }}
            >
              {bezig ? 'Bezig…' : 'Ja, afsluiten'}
            </Knop>
            <Knop nadruk="gewoon" onClick={() => setBevestigen(false)}>
              Nee, nog niet
            </Knop>
          </div>
        </div>
      )}
    </div>
  );
}
